import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import {
  ArrowLeft,
  Download,
  MoreVertical,
  Copy,
  CheckCircle2,
  FileText,
  Loader2,
  Printer,
  Award,
  Clock,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "../ui/dropdown-menu";
import { getEnvelopeSummary, fileUrl, downloadCertificatePdf } from "../../api";
import { recipientColor } from "../../lib/recipientColors";
import DownloadFilesModal from "./DownloadFilesModal";

pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const fmtDateTime = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "—";
  }
};

const initialsOf = (name) =>
  (name || "?")
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0].toUpperCase())
    .join("");

const DetailRow = ({ label, children, testid }) => (
  <div className="flex flex-col gap-1 border-b border-[#EFE9E2] py-3.5 last:border-0 dark:border-white/[0.06] sm:flex-row sm:items-center">
    <span className="w-40 shrink-0 text-sm font-semibold text-[#8B8680] dark:text-white/45">
      {label}
    </span>
    <div className="min-w-0 text-sm text-[#1A1815] dark:text-white" data-testid={testid}>
      {children}
    </div>
  </div>
);

const DocThumb = ({ doc, accent, onOpen }) => {
  const isPdf = (doc.type || "").toUpperCase() === "PDF" && !!doc.storage_path;
  return (
    <button
      data-testid={`document-panel-card-${doc.id}-${doc.doc_index}`}
      onClick={() => onOpen(doc)}
      className="group flex w-full flex-col overflow-hidden rounded-2xl bg-[#F6EFE8] p-3 text-left transition-colors hover:bg-[#F0EBE5] dark:bg-white/[0.04] dark:hover:bg-white/[0.08]"
    >
      <div className="relative mb-3 flex h-44 items-center justify-center overflow-hidden rounded-xl border border-[#E7E0D8] bg-white dark:border-white/10">
        {isPdf ? (
          <Document file={fileUrl(doc.storage_path)} loading={<Loader2 className="h-5 w-5 animate-spin text-[#C9C2BA]" />} error={<FileText className="h-10 w-10 text-[#C9C2BA]" />}>
            <Page pageNumber={1} width={220} renderAnnotationLayer={false} renderTextLayer={false} />
          </Document>
        ) : (
          <FileText className="h-12 w-12 text-[#C9C2BA]" strokeWidth={1.3} />
        )}
        <span className="pointer-events-none absolute inset-0 rounded-xl ring-2 ring-transparent transition-all group-hover:ring-[color:var(--ring)]" style={{ "--ring": accent }} />
      </div>
      <h4 className="line-clamp-1 text-sm font-semibold text-[#1A1815] dark:text-white">
        {doc.name}
      </h4>
      <span className="mt-0.5 text-xs text-[#8B8680] dark:text-white/45">
        {doc.pages} {doc.pages === 1 ? "page" : "pages"}
      </span>
    </button>
  );
};

const EnvelopeSummary = ({ user, company, onAction }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const stateDoc = location.state?.doc;
  const activeCompany = location.state?.company || company;
  const accent = activeCompany?.accent || "#7A6FF0";

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("recipients");
  const [downloadOpen, setDownloadOpen] = useState(false);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    getEnvelopeSummary(id)
      .then((d) => alive && (setData(d), setLoading(false)))
      .catch(() => {
        onAction("Could not open this envelope");
        navigate("/", { replace: true });
      });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const subject = data?.subject || stateDoc?.subject || stateDoc?.name || "Envelope";
  const recipients = useMemo(() => data?.recipients || [], [data]);
  const documents = useMemo(() => data?.documents || [], [data]);
  const isCompleted = data?.envelope_status === "completed";

  const openDocument = (docItem) =>
    navigate(`/view/${docItem.id}`, {
      state: {
        name: docItem.name,
        docIndex: docItem.doc_index,
        docCount: documents.length,
        completed: isCompleted,
        storagePath: docItem.storage_path,
        company: activeCompany,
      },
    });

  const copyEnvelopeId = () => {
    if (!data?.envelope_id) return;
    navigator.clipboard?.writeText(data.envelope_id).then(
      () => onAction("Envelope ID copied"),
      () => onAction("Could not copy")
    );
  };

  const downloadCertificate = () =>
    downloadCertificatePdf(id, `${subject}-certificate.pdf`).catch(() =>
      onAction("Could not download the certificate")
    );

  if (loading || !data) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-[#8B8680] dark:text-white/50" />
      </div>
    );
  }

  return (
    <div className="min-h-screen px-4 pb-16 pt-4 md:px-8" data-testid="envelope-summary-page">
      <div className="mx-auto max-w-[1200px]">
        {/* Header */}
        <div className="flex items-start gap-3">
          <button
            data-testid="summary-back-btn"
            onClick={() => navigate("/")}
            className="mt-1 flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#F0EBE5] text-[#1A1815] transition-colors hover:bg-[#E7E0D8] dark:bg-white/5 dark:text-white dark:hover:bg-white/10"
          >
            <ArrowLeft className="h-4 w-4" strokeWidth={2} />
          </button>
          <div className="min-w-0 flex-1">
            {data.envelope_status === "voided" ? (
              <span className="inline-flex items-center gap-1.5 rounded-full bg-[#E5484D]/15 px-3 py-1 text-xs font-bold text-[#B42318] dark:bg-[#E5484D]/20 dark:text-[#FF8A8A]" data-testid="summary-status-badge">
                Voided
              </span>
            ) : data.envelope_status === "declined" ? (
              <span className="inline-flex items-center gap-1.5 rounded-full bg-[#F2C0C0] px-3 py-1 text-xs font-bold text-[#B42318]" data-testid="summary-status-badge">
                Declined
              </span>
            ) : isCompleted ? (
              <span className="inline-flex items-center gap-1.5 rounded-full bg-[#7AFFA1]/25 px-3 py-1 text-xs font-bold text-[#087443] dark:text-[#6EE7A8]" data-testid="summary-status-badge">
                <CheckCircle2 className="h-3.5 w-3.5" strokeWidth={2.4} />
                Completed
              </span>
            ) : (
              <span className="inline-flex items-center gap-1.5 rounded-full bg-[#F79009]/20 px-3 py-1 text-xs font-bold text-[#B54708] dark:text-[#FDB022]" data-testid="summary-status-badge">
                <Clock className="h-3.5 w-3.5" strokeWidth={2.4} />
                In Progress
              </span>
            )}
            <h1 className="mt-2 truncate text-2xl font-bold tracking-tight text-[#1A1815] dark:text-white" data-testid="summary-title">
              {subject}
            </h1>
          </div>
          {isCompleted && (
          <div className="flex shrink-0 items-center gap-2">
            <button
              data-testid="summary-download-btn"
              onClick={() => setDownloadOpen(true)}
              className="flex h-11 items-center gap-2 rounded-xl bg-[#1A1815] px-5 text-sm font-semibold text-white transition-transform hover:scale-[1.03] active:scale-95 dark:bg-white dark:text-black"
            >
              <Download className="h-4 w-4" strokeWidth={1.9} />
              Download
            </button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid="summary-menu-btn"
                  className="flex h-11 w-11 items-center justify-center rounded-xl border border-[#E7E0D8] text-[#1A1815] transition-colors hover:bg-[#F0EBE5] dark:border-white/10 dark:text-white dark:hover:bg-white/5"
                >
                  <MoreVertical className="h-5 w-5" strokeWidth={2} />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuItem data-testid="menu-download" onClick={() => setDownloadOpen(true)}>
                  <Download className="mr-2 h-4 w-4" /> Download
                </DropdownMenuItem>
                <DropdownMenuItem data-testid="menu-certificate" onClick={downloadCertificate}>
                  <Award className="mr-2 h-4 w-4" /> Certificate of Completion
                </DropdownMenuItem>
                {documents[0] && (
                  <DropdownMenuItem data-testid="menu-print" onClick={() => openDocument(documents[0])}>
                    <Printer className="mr-2 h-4 w-4" /> View &amp; print document
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
          )}
        </div>

        {/* Body: tabs (left) + Documents panel (right) */}
        <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-[1.5fr_1fr]">
          {/* Left: tabs */}
          <div className="glass-panel rounded-[22px] p-5 md:p-6">
            <div className="flex items-center gap-2 border-b border-[#EFE9E2] dark:border-white/10">
              {[
                { key: "recipients", label: `Recipients (${recipients.length})` },
                { key: "details", label: "Details" },
              ].map((t) => (
                <button
                  key={t.key}
                  data-testid={`tab-${t.key}`}
                  onClick={() => setTab(t.key)}
                  className={`relative -mb-px px-4 py-3 text-sm font-semibold transition-colors ${
                    tab === t.key
                      ? "text-[#1A1815] dark:text-white"
                      : "text-[#8B8680] hover:text-[#1A1815] dark:text-white/45 dark:hover:text-white/75"
                  }`}
                >
                  {t.label}
                  {tab === t.key && (
                    <span className="absolute inset-x-2 -bottom-px h-0.5 rounded-full" style={{ backgroundColor: accent }} />
                  )}
                </button>
              ))}
            </div>

            {tab === "recipients" ? (
              <div className="mt-4 space-y-3" data-testid="recipients-list">
                {recipients.map((r, i) => {
                  const rc = recipientColor(r.order_index ?? i);
                  const signed = !!r.signed_at;
                  const isCopy = r.envelope_role === "Receives a Copy";
                  return (
                  <div
                    key={i}
                    data-testid={`recipient-row-${i}`}
                    className="flex items-start gap-3 rounded-2xl border-l-4 bg-[#F6EFE8] px-4 py-3 dark:bg-white/[0.04]"
                    style={{ borderLeftColor: rc }}
                  >
                    {r.avatar ? (
                      <img src={r.avatar} alt={r.name} className="h-10 w-10 rounded-full object-cover ring-2" style={{ "--tw-ring-color": rc, boxShadow: `0 0 0 2px ${rc}` }} />
                    ) : (
                      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-sm font-bold text-white" style={{ backgroundColor: rc }}>
                        {initialsOf(r.name)}
                      </span>
                    )}
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-semibold text-[#1A1815] dark:text-white">{r.name}</p>
                      <p className="truncate text-xs text-[#8B8680] dark:text-white/45">
                        {r.email}
                        {r.role ? ` · ${r.role}` : ""}
                      </p>
                      <div className="mt-2 flex flex-wrap gap-x-4 gap-y-0.5 text-[11px] text-[#8B8680] dark:text-white/40" data-testid={`recipient-timestamps-${i}`}>
                        <span>Sent: {fmtDateTime(r.sent_at)}</span>
                        {r.viewed_at && <span>Viewed: {fmtDateTime(r.viewed_at)}</span>}
                        {signed && <span>Signed: {fmtDateTime(r.signed_at)}</span>}
                      </div>
                    </div>
                    <span
                      className={`mt-0.5 shrink-0 rounded-full px-2.5 py-1 text-[11px] font-bold ${
                        isCopy
                          ? "bg-[#EFE9E2] text-[#8B8680] dark:bg-white/10 dark:text-white/55"
                          : signed
                          ? "bg-[#7AFFA1]/25 text-[#087443] dark:text-[#6EE7A8]"
                          : r.status === "declined"
                          ? "bg-[#F2C0C0] text-[#B42318]"
                          : "bg-[#F79009]/20 text-[#B54708] dark:text-[#FDB022]"
                      }`}
                    >
                      {isCopy ? "Receives a Copy" : signed ? "Signed" : r.status === "declined" ? "Declined" : "Pending"}
                    </span>
                  </div>
                  );
                })}
                {recipients.length === 0 && (
                  <p className="py-6 text-center text-sm text-[#8B8680] dark:text-white/40">No recipients.</p>
                )}
              </div>
            ) : (
              <div className="mt-2" data-testid="details-list">
                <DetailRow label="Envelope Name" testid="detail-name">{subject}</DetailRow>
                <DetailRow label="Envelope ID" testid="detail-envelope-id">
                  <div className="flex items-center gap-2">
                    <span className="truncate font-mono text-xs">{data.envelope_id}</span>
                    <button
                      data-testid="copy-envelope-id-btn"
                      onClick={copyEnvelopeId}
                      title="Copy Envelope ID"
                      className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-[#8B8680] transition-colors hover:bg-[#F0EBE5] hover:text-[#1A1815] dark:text-white/45 dark:hover:bg-white/10 dark:hover:text-white"
                    >
                      <Copy className="h-3.5 w-3.5" strokeWidth={2} />
                    </button>
                  </div>
                </DetailRow>
                <DetailRow label="From" testid="detail-from">
                  <p className="font-semibold">{data.originator?.name}</p>
                  <p className="text-xs text-[#8B8680] dark:text-white/45">{data.originator?.email}</p>
                </DetailRow>
                <DetailRow label="Sent" testid="detail-sent">{fmtDateTime(data.sent_at)}</DetailRow>
                <DetailRow label="Last Changed" testid="detail-last-changed">{fmtDateTime(data.last_changed)}</DetailRow>
                <DetailRow label="Folder" testid="detail-folder">{data.folder}</DetailRow>
              </div>
            )}
          </div>

          {/* Right: Documents panel */}
          <div className="glass-panel rounded-[22px] p-5 md:p-6" data-testid="documents-panel">
            <h3 className="mb-4 text-lg font-bold tracking-tight text-[#1A1815] dark:text-white">
              Documents
            </h3>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-1">
              {documents.map((d) => (
                <DocThumb key={`${d.id}-${d.doc_index}`} doc={d} accent={accent} onOpen={openDocument} />
              ))}
            </div>
          </div>
        </div>
      </div>

      <DownloadFilesModal
        open={downloadOpen}
        onClose={() => setDownloadOpen(false)}
        docId={id}
        baseName={subject}
        docCount={documents.length}
        onAction={onAction}
      />
    </div>
  );
};

export default EnvelopeSummary;
